import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { IRootState } from 'src/reducers';
import PersonnelSearch from '../components/PersonnelSearch';
import Scorecard from 'src/models/personnel/Scorecard';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import moment from 'moment';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ScorecardForm from 'src/models/personnel/ScorecardForm';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import ScorecardDocs from './ScorecardDocs';
import ScorecardCommentDialog from '../dialogs/ScorecardCommentDialog';
import PersonnelScorecard from 'src/models/personnel/PersonnelScorecard';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';
import PersonnelToast from '../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';

const ScorecardPage = () => {
    const { register, errors, getValues, handleSubmit, setValue } = useForm();
    const { get, post, response } = useFetch();
    const dispatch = useDispatch();
    let selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isTableHidden, setIsTableHidden] = useState<boolean>(true);
    const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
    const [scorecardsList, setScorecardsList] = useState<Scorecard[]>();
    const [globalScorecardsList, setGlobalScorecardsList] = useState<PersonnelScorecard[]>();
    const [scorecardComment, setScorecardComment] = useState<string>();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);

    const newScorecard: Scorecard[] = [
        { id: 0, userId: selectedPersonnel?.userId || 0, createdDate: moment(new Date()).format('YYYY-MM-DD') as unknown as Date, canTravel: false, canInHouse: false, 
        canMetesBounds: false, titleRating: 0, inHouseRating: 0,  acquisitionsRating: 0, rowRating: 0, dueDiligenceRating: 0, curativeHeirshipRating: 0, 
        systemsDataEntryRating: 0, peopleLikeabilityRating: 0, serviceDependabilityRating: 0, productRating: 0, coreValueAlignmentRating: 0, grade: 0, 
        playerRating: '', comment: 'New Scorecard', year: ''}
    ];

    // Initial render
    useEffect(() => {
        onCreate();
        
        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (!!selectedPersonnel) {
            getScorecardsById('New Scorecard');
            getAllScorecards();
            console.log('====> SelectedPersonnel = ', selectedPersonnel);
        }
    }, [selectedPersonnel]);

    useEffect(() => {
        if (!!scorecardsList) {
            initializeData(scorecardsList);
            console.log('====> ScorecardsList', scorecardsList);
        }    
    }, [scorecardsList]);

    useEffect(() => {
        console.log('====> ScorecardComment', scorecardComment);
        if (scorecardComment) {
            setValue(`scorecardsList[${scorecardsList?.length - 1}].comment`, scorecardComment);
        }
    }, [scorecardComment]);

    const onCreate = () => {
        console.log('ScorecardPage onCreate()');
        getScorecardsById('New Scorecard');
        getAllScorecards();
    };

    const onDestroy = () => {
        console.log('ScorecardPage onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color  });
    };

    const sortScorecards = (array: Scorecard[]) => {
        return array?.sort((a, b) => a.id - b.id);
    };

    /**
     * Gets the Scorecards for the selectedPersonnel if one is selected
     * @param comment - Sets the New Scorecard comment to this string
     */
    const getScorecardsById = async (comment: string) => {
        if (selectedPersonnel) {
            let result = await get(`/v1.0/landmen/personnel/${selectedPersonnel?.userId}/scorecard`);
            if (response.ok) {
                if (result && Array.isArray(result)) {
                    // It should never be null or undefined, as it is hardcoded
                    result = sortScorecards(result);
                    if (newScorecard[0] !== null && newScorecard[0] !== undefined) {
                        newScorecard[0].comment = comment;
                        setScorecardComment(comment);
                        result.push(newScorecard[0]);
                    }
                    setScorecardsList(result);
                } else {
                    setScorecardsList(newScorecard);
                }
            } else {
                console.log('Unable to get the Personnel Data!!! >:c');
                setScorecardsList(newScorecard);
            }
        }
    };

    const getAllScorecards = async () => {
        if (selectedPersonnel) {
            let result = await get(`/v1.0/landmen/personnel/scorecard`);
            if (response.ok) {
                if (result && Array.isArray(result)) {
                    result = sortScorecards(result);
                    setGlobalScorecardsList(result);
                }
            } else {
                console.log('Unable to get Scorecards!!! >:c');
            }
        }
    };

    const initializeData = (data: Scorecard[]) => {
        let newData: Scorecard[] = [...data];

        setValue(`scorecardsList[${scorecardsList?.length - 1}].grade`, 0);
        setValue(`scorecardsList[${scorecardsList?.length - 1}].playerRating`, '');
        setValue(`scorecardsList[${scorecardsList?.length - 1}].comment`, 'New Scorecard');
        newScorecard[0].grade = 0;
        newScorecard[0].playerRating = '';
        newScorecard[0].comment = 'New Scorecard';

        return newData;
    };

    const onSubmit = async (data: ScorecardForm) => {
        if (!!data && !!selectedPersonnel) {
            let temp = finalizeData(data);
            let newestScorecard = temp.scorecardsList?.pop();
            newestScorecard.userId = selectedPersonnel?.userId;
            
            createScorecard(newestScorecard);
        }
    };

    const getPlayerRating = (grade: number) => {
        if (grade >= 9.5) { 
            return 'A+';
        } else if (grade >= 9) { 
            return 'A';
        } else if (grade >= 8.5) {
            return 'B+';
        } else if (grade >= 8) {
            return 'B';
        } else if (grade >= 7.5) {
            return 'C+';
        } else if (grade >= 7) {
            return 'C';
        } else if (grade >= 6.5) {
            return 'D+';
        } else if (grade >= 6) {
            return 'D';
        } else if (grade >= 5.5) {
            return 'F+';
        } else {
            return 'F';
        } 
    };

    const finalizeData = (data: ScorecardForm) => {
        let finalData: ScorecardForm = {
            ...data,
            scorecardsList: data.scorecardsList,
        };

        for (let i = 0; i < finalData.scorecardsList?.length; i++) { 
            finalData.scorecardsList[i].canTravel = finalData.scorecardsList[i].canTravel ? true : false;
            finalData.scorecardsList[i].canInHouse = finalData.scorecardsList[i].canInHouse ? true : false;
            finalData.scorecardsList[i].canMetesBounds = finalData.scorecardsList[i].canMetesBounds ? true : false;
            finalData.scorecardsList[i] = calculateGrade(finalData.scorecardsList[i]);
        }

        return finalData;
    };

    const createScorecard = async (data: Scorecard) => {
        let result = await post(`/v1.0/landmen/personnel/scorecard`, data);

        if (response.ok) {
            showToast('Update successful');
            getScorecardsById('New Scorecard');
            getAllScorecards();
        } else if (result?.messageCode === '15') {
            showToast('Unable to create scorecard', `rgba(255,0,0,0.5)`);
        } else {
            showToast('Unable to create scorecard', `rgba(255,0,0,0.5)`);
        }
    };

    // const updateScorecard = async (data: ScorecardForm) => {
    //     let result = await put(`/v1.0/landmen/personnel/${selectedPersonnel?.id}/scorecard`, data);

    //     if (response.ok) {
    //         window.alert('Update successful');
    //     } else if (result?.messageCode === '15') {
    //         window.alert('Update failed');
    //     } else {
    //         window.alert('Update Personnel failed');
    //     }
    // };

    const showPersonnelData = () => {
        console.log('Display the Stuffs');
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Scorecard - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Scorecard' ;

        return title;
    };

    /**
     * Calculates the grade (Average) based on the 11 individual ratings
     * @param sc or Scorecard
     * @returns The scorecard with the new grade and playerRating
     */
    const calculateGrade = (sc: Scorecard) => {
        let temp = sc;
        let count = 0;

        temp.createdDate = sc.createdDate;

        // Count how many ratings have been selected for the average
        temp.titleRating = parseInt(temp.titleRating + '', 10);
        temp.titleRating && temp.titleRating > 0 ? count++ : temp.titleRating = 0 ;

        temp.inHouseRating = parseInt(temp.inHouseRating + '', 10);
        temp.inHouseRating && temp.inHouseRating > 0 ? count++ : temp.inHouseRating = 0 ;

        temp.acquisitionsRating = parseInt(temp.acquisitionsRating + '', 10);
        temp.acquisitionsRating && temp.acquisitionsRating > 0 ? count++ : temp.acquisitionsRating = 0 ;

        temp.rowRating = parseInt(temp.rowRating + '', 10);
        temp.rowRating && temp.rowRating > 0 ? count++ : temp.rowRating = 0 ;

        temp.dueDiligenceRating = parseInt(temp.dueDiligenceRating + '', 10);
        temp.dueDiligenceRating && temp.dueDiligenceRating > 0 ? count++ : temp.dueDiligenceRating = 0 ;

        temp.curativeHeirshipRating = parseInt(temp.curativeHeirshipRating + '', 10);
        temp.curativeHeirshipRating && temp.curativeHeirshipRating > 0 ? count++ : temp.curativeHeirshipRating = 0 ;

        temp.systemsDataEntryRating = parseInt(temp.systemsDataEntryRating + '', 10);
        temp.systemsDataEntryRating && temp.systemsDataEntryRating > 0 ? count++ : temp.systemsDataEntryRating = 0 ;

        temp.peopleLikeabilityRating = parseInt(temp.peopleLikeabilityRating + '', 10);
        temp.peopleLikeabilityRating && temp.peopleLikeabilityRating > 0 ? count++ : temp.peopleLikeabilityRating = 0 ;

        temp.serviceDependabilityRating = parseInt(temp.serviceDependabilityRating + '', 10);
        temp.serviceDependabilityRating && temp.serviceDependabilityRating > 0 ? count++ : temp.serviceDependabilityRating = 0 ;

        temp.productRating = parseInt(temp.productRating + '', 10);
        temp.productRating && temp.productRating > 0 ? count++ : temp.productRating = 0 ;

        temp.coreValueAlignmentRating = parseInt(temp.coreValueAlignmentRating + '', 10);
        temp.coreValueAlignmentRating && temp.coreValueAlignmentRating > 0 ? count++ : temp.coreValueAlignmentRating = 0 ;

        // Add the total of the selected ratings
        temp.grade = temp.titleRating + temp.inHouseRating + temp.acquisitionsRating + temp.rowRating + temp.dueDiligenceRating + temp.curativeHeirshipRating + 
        temp.systemsDataEntryRating + temp.peopleLikeabilityRating + temp.serviceDependabilityRating + temp.productRating + temp.coreValueAlignmentRating;

        // Find average rating by dividing by the total number of ratings selected (grade)
        count > 0 ? temp.grade = temp.grade / count : temp.grade = 0;
        temp.playerRating = getPlayerRating(temp.grade);

        return temp;
    };

    // Calculate the grade based on sum of individual ratings.
    const onRatingChanged = (index: number) => {
        if (!!scorecardsList) {
            let values = getValues(); 

            let temp = calculateGrade(values.scorecardsList[index]);
            setValue(`scorecardsList[${index}].grade`, temp?.grade );
            setValue(`scorecardsList[${index}].playerRating`, temp?.playerRating);
        }
    };

    const expandComment = (e: React.MouseEvent<HTMLInputElement>) => {
        let value = getValues(`scorecardsList[${scorecardsList?.length - 1}].comment`);
        dispatch(actions.layout.showDialog(DialogList.SCORECARD_COMMENT));
        setScorecardComment(value);
        setIsDialogVisible(true);
    };

    const handleComment = (comment: string) => {
        if (comment === '') {
            comment = ' ';
        }
        setScorecardComment(comment);
    };

    const handleCheckboxChange = (id: number, field: keyof Scorecard) => {
        setScorecardsList(
            scorecardsList?.map(sc =>
                sc.id === id ? { ...sc, [field]: !sc[field] } : sc
            )
        );
    };

    const showDialog = (dialog: DialogList) => {
        dispatch(actions.layout.showDialog(dialog));
    };

    const hideDialog = () => {
        setIsDialogVisible(false);
        dispatch(actions.layout.hideDialog());
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const displayAllScorecardInfo = () => {
        return <div style={{overflowX: 'auto', overflowY: 'hidden', paddingTop: '1rem', paddingBottom: '2rem'}}>
            <table hidden={isTableHidden}>
                <thead>
                    <tr>
                        <th className="diagonal-header">Name</th>
                        <th className="diagonal-header">Date</th>
                        <th className="diagonal-header">Travel</th>
                        <th className="diagonal-header">In-House</th>
                        <th className="diagonal-header">Metes & Bounds</th>
                        <th className="diagonal-header">Title</th>
                        <th className="diagonal-header">In-House</th>
                        <th className="diagonal-header">Acqs</th>
                        <th className="diagonal-header">ROW</th>
                        <th className="diagonal-header">Due Diligence</th>
                        <th className="diagonal-header">Curative & Heirship</th>
                        <th className="diagonal-header">Systems & Data Entry</th>
                        <th className="diagonal-header">People & Likeability</th>
                        <th className="diagonal-header">Service & Depend.</th>
                        <th className="diagonal-header">Product</th>
                        <th className="diagonal-header">Core Value</th>
                        <th className="diagonal-header">Grade</th>
                        <th className="diagonal-header">Player</th>
                        <th className="diagonal-header">Comment</th>
                    </tr>
                </thead>
                <div style={{height: '2rem'}}/>
                {Array.isArray(globalScorecardsList) && globalScorecardsList?.map((sc: PersonnelScorecard, index: number) => {
                    return (
                        <tbody key={sc.scorecard.id}>
                            <tr>
                                <td style={{width: '10%'}}>
                                    <label>{`${sc.personnel.firstName} ${sc.personnel.lastName}`}</label>
                                </td>
                                <td>
                                    <RHFInputBox 
                                        name={`globalScorecardsList[${index}].scorecard.createdDate`} 
                                        type="date"
                                        defaultVal={`${moment(sc.scorecard.createdDate).format('YYYY-MM-DD') as unknown as Date}`}
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        disabled={true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFCheckbox
                                        name={`globalScorecardsList[${index}].scorecard.canTravel`} 
                                        register={register} 
                                        errors={errors}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                        bodyStyle={{textAlign: 'center'}}
                                        checked={sc.scorecard.canTravel}
                                        onChange={(e) => handleCheckboxChange(sc.scorecard.id, 'canTravel')}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFCheckbox 
                                        name={`globalScorecardsList[${index}].scorecard.canInHouse`} 
                                        register={register} 
                                        errors={errors}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                        bodyStyle={{textAlign: 'center'}}
                                        checked={sc.scorecard.canInHouse}
                                        onChange={(e) => handleCheckboxChange(sc.scorecard.id, 'canInHouse')}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFCheckbox 
                                        name={`globalScorecardsList[${index}].scorecard.canMetesBounds`} 
                                        register={register} 
                                        errors={errors}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                        bodyStyle={{textAlign: 'center'}}
                                        checked={sc.scorecard.canMetesBounds}
                                        onChange={(e) => handleCheckboxChange(sc.scorecard.id, 'canMetesBounds')}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.titleRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.titleRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.inHouseRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.inHouseRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.acquisitionsRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.acquisitionsRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.rowRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.rowRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.dueDiligenceRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.dueDiligenceRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.curativeHeirshipRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.curativeHeirshipRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.systemsDataEntryRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.systemsDataEntryRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.peopleLikeabilityRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.peopleLikeabilityRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.serviceDependabilityRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.serviceDependabilityRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.productRating`}
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.productRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`globalScorecardsList[${index}].scorecard.coreValueAlignmentRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.scorecard.coreValueAlignmentRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFInputBox 
                                        name={`globalScorecardsList[${index}].scorecard.grade`} 
                                        type="number"
                                        defaultVal={`${sc.scorecard.grade}`}
                                        register={register} 
                                        errors={errors}
                                        style={sc.scorecard.id === 0 ? {width: '100%', borderBottom: '2px solid blue'} : {width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        disabled={true}
                                    />
                                </td>
                                <td style={{width: '3%'}}>
                                    <RHFInputBox 
                                        name={`globalScorecardsList[${index}].scorecard.playerRating`} 
                                        type="text"
                                        defaultVal={`${sc.scorecard.playerRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={sc.scorecard.id === 0 ? {width: '100%', borderBottom: '2px solid blue'} : {width: '100%'}}
                                        bodyStyle={{width: '70%'}}
                                        disabled={true}
                                    />
                                </td>
                                <td style={{width: '12%'}}>
                                    <RHFInputBox 
                                        name={`globalScorecardsList[${index}].scorecard.comment`} 
                                        type="text"
                                        title={`${sc.scorecard.comment}`}
                                        defaultVal={`${sc.scorecard.comment}`}
                                        register={register} 
                                        errors={errors}
                                        style={sc.scorecard.id === 0 ? {width: '100%', border: '2px solid blue'} : {width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                        onClick={(e) => expandComment(e)}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <Button
                                        className="blue-hover-button"
                                        style={{ height: '3rem'}}
                                        text="Save"
                                        type="submit"
                                        form="scorecardForm"
                                        disabled={sc.scorecard.id === 0 ? false : true}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    );
                })}
            </table>
        </div>;
    };

    const displayScorecardInfo = () => {
        return <div>
            <table>
                <thead>
                    <tr>
                        <th className="diagonal-header">Date</th>
                        <th className="diagonal-header">Travel</th>
                        <th className="diagonal-header">In-House</th>
                        <th className="diagonal-header">Metes & Bounds</th>
                        <th className="diagonal-header">Title</th>
                        <th className="diagonal-header">In-House</th>
                        <th className="diagonal-header">Acqs</th>
                        <th className="diagonal-header">ROW</th>
                        <th className="diagonal-header">Due Diligence</th>
                        <th className="diagonal-header">Curative & Heirship</th>
                        <th className="diagonal-header">Systems & Data Entry</th>
                        <th className="diagonal-header">People & Likeability</th>
                        <th className="diagonal-header">Service & Depend.</th>
                        <th className="diagonal-header">Product</th>
                        <th className="diagonal-header">Core Value</th>
                        <th className="diagonal-header">Grade</th>
                        <th className="diagonal-header">Player</th>
                        <th className="diagonal-header">Comment</th>
                    </tr>
                </thead>
                <div style={{height: '2rem'}}/>
                {Array.isArray(scorecardsList) && scorecardsList?.map((sc: Scorecard, index: number) => {
                    return (
                        <tbody key={sc.id}>
                            <tr>
                                <td style={{width: '4%'}}>
                                    <RHFInputBox 
                                        name={`scorecardsList[${index}].createdDate`} 
                                        type="date"
                                        defaultVal={`${moment(sc.createdDate).format('YYYY-MM-DD') as unknown as Date}`}
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        disabled={true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFCheckbox
                                        name={`scorecardsList[${index}].canTravel`} 
                                        register={register} 
                                        errors={errors}
                                        disabled={sc.id === 0 ? false : true}
                                        bodyStyle={{textAlign: 'center'}}
                                        checked={sc.canTravel}
                                        onChange={(e) => handleCheckboxChange(sc.id, 'canTravel')}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFCheckbox 
                                        name={`scorecardsList[${index}].canInHouse`} 
                                        register={register} 
                                        errors={errors}
                                        disabled={sc.id === 0 ? false : true}
                                        bodyStyle={{textAlign: 'center'}}
                                        checked={sc.canInHouse}
                                        onChange={(e) => handleCheckboxChange(sc.id, 'canInHouse')}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFCheckbox 
                                        name={`scorecardsList[${index}].canMetesBounds`} 
                                        register={register} 
                                        errors={errors}
                                        disabled={sc.id === 0 ? false : true}
                                        bodyStyle={{textAlign: 'center'}}
                                        checked={sc.canMetesBounds}
                                        onChange={(e) => handleCheckboxChange(sc.id, 'canMetesBounds')}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].titleRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.titleRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].inHouseRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.inHouseRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].acquisitionsRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.acquisitionsRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].rowRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.rowRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].dueDiligenceRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.dueDiligenceRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].curativeHeirshipRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.curativeHeirshipRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].systemsDataEntryRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.systemsDataEntryRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].peopleLikeabilityRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.peopleLikeabilityRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].serviceDependabilityRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.serviceDependabilityRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].productRating`}
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.productRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFSelect 
                                        name={`scorecardsList[${index}].coreValueAlignmentRating`} 
                                        options={[
                                            { value: 0, label: 'Select' },
                                            { value: 10, label: '10' },
                                            { value: 9, label: '9' },
                                            { value: 8, label: '8' },
                                            { value: 7, label: '7' },
                                            { value: 6, label: '6' },
                                            { value: 5, label: '5' },
                                            { value: 4, label: '4' },
                                            { value: 3, label: '3' },
                                            { value: 2, label: '2' },
                                            { value: 1, label: '1' },
                                        ]}
                                        defaultVal={`${sc.coreValueAlignmentRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        onChange={() => onRatingChanged(index)}
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <RHFInputBox 
                                        name={`scorecardsList[${index}].grade`} 
                                        type="number"
                                        defaultVal={`${sc.grade}`}
                                        register={register} 
                                        errors={errors}
                                        style={sc.id === 0 ? {width: '100%', borderBottom: '2px solid blue'} : {width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        disabled={true}
                                    />
                                </td>
                                <td style={{width: '3%'}}>
                                    <RHFInputBox 
                                        name={`scorecardsList[${index}].playerRating`} 
                                        type="text"
                                        defaultVal={`${sc.playerRating}`}
                                        register={register} 
                                        errors={errors}
                                        style={sc.id === 0 ? {width: '100%', borderBottom: '2px solid blue'} : {width: '100%'}}
                                        bodyStyle={{width: '70%'}}
                                        disabled={true}
                                    />
                                </td>
                                <td style={{width: '12%'}}>
                                    <RHFInputBox 
                                        name={`scorecardsList[${index}].comment`} 
                                        type="text"
                                        title={`${sc.comment}`}
                                        defaultVal={`${sc.comment}`}
                                        register={register} 
                                        errors={errors}
                                        style={sc.id === 0 ? {width: '100%', border: '2px solid blue'} : {width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        disabled={sc.id === 0 ? false : true}
                                        onClick={(e) => expandComment(e)}
                                    />
                                </td>
                                <td style={{width: '5%'}}>
                                    <Button
                                        className="blue-hover-button"
                                        style={{ height: '3rem'}}
                                        text="Save"
                                        type="submit"
                                        form="scorecardForm"
                                        disabled={sc.id === 0 ? false : true}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    );
                })}
            </table>
        </div>;
    };

    return (
        <div>
            <PersonnelNavBar/>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch showPersonnelData={showPersonnelData} setIsCollapsed={setIsCollapsed} toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <form id="scorecardForm" onSubmit={handleSubmit(onSubmit)}>
                <Page className="dlc-maxer-width">
                    <Page.Section>
                        <Panel backgroundColor="#D3D3D3"> 
                            <Panel panelTitle={displayTitle()}>
                                {selectedPersonnel && <div className={isCollapsed ? 'medium-small-panel' : 'medium-small-panel-with-search'} style={{overflowY: 'scroll'}}>
                                    <fieldset className="legend-divider">
                                        <legend className="legend-title">&nbsp;&nbsp;{`${selectedPersonnel?.fName} ${selectedPersonnel?.lName}'s`} Scorecards&nbsp;&nbsp;</legend>
                                    </fieldset>
                                    {displayScorecardInfo()}
                                    <fieldset className="legend-divider" style={{marginTop: '2rem'}}>
                                        <legend className="legend-title">&nbsp;
                                            <label className="expand-collapse" onClick={() => setIsTableHidden(!isTableHidden)}>
                                                {isTableHidden ? ' + ' : ' - '}
                                            </label>
                                            &nbsp;All Scorecards&nbsp;&nbsp;
                                        </legend>
                                    </fieldset>
                                    {!isTableHidden ? displayAllScorecardInfo() : console.log('All Scorecards Hidden')}
                                </div>}
                                {!selectedPersonnel && <div className="bold-centered">No Personnel Selected</div>}
                            </Panel>
                            <Panel>
                                { selectedPersonnel && <ScorecardDocs
                                    showDialog={showDialog}
                                    hideDialog={hideDialog}
                                    hasOwnership={false}
                                    userId={selectedPersonnel ? selectedPersonnel.userId : null}
                                />}
                            </Panel>
                        </Panel> 
                    </Page.Section>
                </Page>
            </form>
            <ScorecardCommentDialog 
                dialogName={DialogList.SCORECARD_COMMENT} 
                isDialogVisible={isDialogVisible}
                scorecardComment={scorecardComment} 
                handleComment={handleComment} 
                hideDialog={hideDialog}
            />
            <PersonnelAdvancedQuickFind 
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog} 
                selectedPersonnel={selectedPersonnel}    
                selectPersonnel={setSelectedPersonnel}        
            />
        </div>
    );
};

export default ScorecardPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/ScorecardPage.tsx